import i18n from '@/plugins/vue-i18n';
import { datadogRum } from '@datadog/browser-rum';
import authConfig from '@sharedModules/config/staticAuth';
import to from 'await-to-js';
import axios from 'axios';
import { get, includes } from 'lodash';
import numeral from 'numeral';
import { defineStore } from 'pinia';
import { isEmbedded } from '@/webshell-embed/embedded';
import { shellBridge } from '@/webshell-embed/platform-bridge';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useEventsStore } from './events';

// Embedded, the shell is the single auth authority - ADR-003 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/003-centralize-auth-in-platform-auth-service.md
// and issues the shared platform JWT cookie; AO issues no tokens. AO runs in the shell's document
// and can't POST the shell's refresh endpoint cross-origin, so it asks the bridge to broker it.
// Standalone keeps AO's own route.
const STANDALONE_TOKEN_REFRESH_URL = '/api/token/refresh';

const getInitialContextState = () => ({
  loginMethod: null, // primary authMethod from auth.js config file
  loginRedirectUrl: null,
  oktaClientId: null,
  versionTag: null,
  env: null,
  subBrand: null,
  datadog: {},
  clientUrl: null,
  powerBIreportIds: {},
  // TODO Remove this once AOV3-8139 is done
  appKey: 0, // eventually we need to recreate the components when the locales are changed

  // client configuration
  clientConfig: {},
  features: {}, // use to check if feature is enabled
});

const getInitialUserContextState = () => ({
  csrfToken: null,
  profile: null,
  loading: false,
  currentLoginMethod: null, // authMethod used on session scope, needed for correct logout
  expireAt: null,
  showAdminControls: false,
  showClientAdminControls: false,
  simpleSwapsEnabled: false,
  reportingEnabled: false,
  templatesEnabled: false,
  bundlesEnabled: false,
  storeExecutionEnabled: false,
  uom: null,
  weeksNotSoldForDiscontinued: null,
  notUsingLocalDatabase: false,
  i18n: null,
  availableLocales: null,
  availableNumericLocales: null,
  availableDateLocales: null,
  currentLocale: null,
  numericLocale: null,
  dateLocale: null,
  localeInfo: null,
  // used by unsaved-data-warning
  allowTabChange: true,
  compoundExtractsEnabled: false,
  reportingPowerBIEnabled: false,
  availableExtractsExports: [],
  // AI terms acceptance — null means not yet accepted, ISO date string means accepted
  aiTermsAcceptedAt: null,
  // tenant management
  availableTenants: [],
  currentTenant: null,
});

export const useContextStore = defineStore('context', {
  state: () => ({
    ...getInitialContextState(),
    ...getInitialUserContextState(),
  }),
  getters: {
    currentUserPermissions: state => new Set(get(state.profile, 'permissions', [])), // Use set for quicker lookup
    currentUserProfile: state => state.profile,
    getFullName: state => {
      const firstName = get(state, 'profile.firstname', '');
      const surname = get(state, 'profile.surname', '');
      return `${firstName} ${surname}`.trim();
    },
    hasPermission() {
      return permission => this.currentUserPermissions.has(permission);
    },
    getDateFormats: state => state.clientConfig.dateFormats[state.dateLocale],
    getExcelDateFormat: state =>
      state.clientConfig.dateFormats[state.dateLocale].long.replace(/-/g, '/'),
    getDefaultReverseFormat: state => state.clientConfig.dateFormats.defaultReverse,
    getTimeRemainingFormat: state => state.timeRemainingFormat,
    getCurrency: state => state.clientConfig.currency,
    getCsvDelimiter: state => state.clientConfig.csvDelimiter,
    getCsvExport: state => state.clientConfig.csvExport,
    getCannGroupsRecommendedSizes: state => state.clientConfig.cannGroupsRecommendedSizes,
    getShowAdminControlsPermissions: state => state.showAdminControls,
    getShowClientAdminControlsPermissions: state => state.showClientAdminControls,
    getShowReportingControlsPermissions: state => state.reportingEnabled,
    getShowTemplatesPermissions: state => state.templatesEnabled,
    getShowBundlesPermissions: state => state.bundlesEnabled,
    getSimpleSwapsPermissions: state => state.simpleSwapsEnabled,
    getStoreExecutionPermissions: state => state.storeExecutionEnabled,
    getUnitOfMeasure: state => state.uom,
    getClientConfig: state => state.clientConfig,
    getAvailableLocales: state => state.availableLocales,
    getCurrentNumericLocale: state => state.numericLocale,
    getAvailableNumericLocales: state => state.availableNumericLocales,
    getCurrentDateLocale: state => state.dateLocale,
    getAvailableDateLocales: state => state.availableDateLocales,
    getLocaleInfo: state => state.localeInfo,
    getCurrentLocale: state => state.currentLocale,
    showNotImplemented: state => get(state.clientConfig, 'features.showNotImplemented', false),
    getShowCompoundExtractsPermissions: state => state.compoundExtractsEnabled,
    getShowReportingPowerBIPermissions: state => state.reportingPowerBIEnabled,
    getAvailableExtractsExports: state => state.availableExtractsExports,
    getProductMinimumFacings: state => state.clientConfig.productMinimumFacings,
    hasAcceptedAiTerms: state => !!state.aiTermsAcceptedAt,
    // TODO move this from context store to useFeature() composable
    getPalletsSlotsEnabled: state => get(state.clientConfig, 'features.palletsSlotsEnabled', false),
    getDefaultOptimiserWeights: state =>
      get(state.clientConfig, 'scenario.assortmentCanvas.defaultOptimiserWeights'),
    // tenant getters
    getAvailableTenants: state => state.availableTenants || [],
    isMultitenancyEnabled: state => state.availableTenants && state.availableTenants.length > 1,
    getCurrentTenant: state => state.currentTenant,
  },
  actions: {
    setContext(contextData) {
      Object.assign(this, contextData);
    },
    loginHardcoded(credentials) {
      return axios.post('/api/login', credentials).then(
        () => {
          this.currentLoginMethod = 'ow-auth-hardcoded';
          return this.loadUserContext();
        },
        err => {
          console.error(err.response.data.message);
          this.currentLoginMethod = 'ow-auth-hardcoded';
        }
      );
    },

    loginOkta(token) {
      return axios.post('/api/ow-auth/login', { token }).then(
        () => {
          this.currentLoginMethod = 'ow-auth-okta';
          return this.loadUserContext();
        },
        err => {
          console.error(err.response.data.message);
          this.currentLoginMethod = 'ow-auth-okta';
        }
      );
    },

    async loadAppContext() {
      const [err, res] = await to(axios.get('/api/app-context'));
      if (err) throw new Error(err.message);

      const { data } = res;
      if (!data) return;

      this.setContext(data);
    },

    async initialiseState() {
      // TODO: better way to pass env var to FE
      const [, res] = await to(axios.get('/api/events/rabbit-enabled'));
      if (get(res, 'data') === true) {
        const eventsStore = useEventsStore();
        eventsStore.eventstreamEnabled = true;
        eventsStore.resetEventStream();
      }
    },

    async loadUserContext(requestConfig) {
      const [err, res] = await to(axios.get('/api/user-context', requestConfig));
      if (err) throw new Error(err.message);

      const { data } = res;
      if (!data) return;
      datadogRum.setUser({ id: data.profile._id, name: data.profile.username });
      // save tokenLifeTime in milliseconds if valid login exists
      const tokenLifetime = (data.exp - data.iat) * 1000;
      const expirationDate = Date.now() + tokenLifetime;
      this.profile = data.profile;
      this.csrfToken = data.csrfToken;
      this.expireAt = expirationDate;
      this.showAdminControls = data.showAdminControls;
      this.showClientAdminControls = data.showClientAdminControls;
      this.simpleSwapsEnabled = data.simpleSwapsEnabled;
      this.reportingEnabled = data.reportingEnabled;
      this.templatesEnabled = data.templatesEnabled;
      this.bundlesEnabled = data.bundlesEnabled;
      this.storeExecutionEnabled = data.storeExecutionEnabled;
      this.compoundExtractsEnabled = data.compoundExtractsEnabled;
      this.reportingPowerBIEnabled = data.reportingPowerBIEnabled;
      this.availableExtractsExports = data.availableExtractsExports;
      this.availableDateLocales = data.availableDateLocales;
      this.aiTermsAcceptedAt = data.profile.aiTermsAcceptedAt || null;

      if (data.currentLoginMethod) {
        this.currentLoginMethod = data.currentLoginMethod;
      }

      // load tenants data
      this.setAvailableTenants(data.tenants || []);
      this.setCurrentTenant(data.currentTenant);

      // We get translations here as they contain sensitive info
      this.i18n = data.i18nConfig;
      this.setNumericLocale({});
      this.setDateLocale({});
      await this.loadAppContext();
      await this.setLanguageLocale({});

      // this is where we know the user has successfully authenticated so we can load state
      this.initialiseState(null);
    },

    // Set the language provided so that translations for that language can be extracted
    setLanguageLocale({ locale }) {
      const availableLocales = Object.keys(this.i18n.translations);
      this.availableLocales = availableLocales;
      availableLocales.forEach(availableLocale =>
        i18n.setLocaleMessage(availableLocale, this.i18n.translations[availableLocale])
      );

      const { localeInfo } = this.i18n; // contains abbreviations and full names of available locales
      this.localeInfo = localeInfo;
      const previousLocale = this.currentLocale;

      const isCurrentLocaleAvailable =
        this.currentLocale && includes(availableLocales, this.currentLocale);
      const isDefaultLocaleAvailable = includes(availableLocales, this.i18n.defaultLocale);
      // If current locale is not available, set new locale to default or fallback.
      if (!isCurrentLocaleAvailable) {
        i18n.global.locale.value = isDefaultLocaleAvailable
          ? this.i18n.defaultLocale
          : this.i18n.fallbackLocale;
      } else {
        // Otherwise, only update it to provided or current locale on change.
        i18n.global.locale.value = locale || this.currentLocale;
      }
      this.currentLocale = i18n.global.locale.value;
      // In vue 3 vue-i18n currently is not reactive when changing locales so we need to refresh the components
      // TODO Remove this once AOV3-8139 is done
      if (previousLocale !== this.currentLocale) this.appKey += 1;
    },
    setNumericLocale({ locale }) {
      const availableLocales = this.i18n.availableNumericLocales;
      this.availableNumericLocales = availableLocales;
      const previousNumericLocale = this.numericLocale;

      const isNumericLocaleAvailable =
        this.numericLocale && includes(availableLocales, this.numericLocale);
      const isDefaultNumericLocaleAvailable = includes(
        availableLocales,
        this.i18n.defaultNumericLocale
      );
      // If current current locale is not available, set new current locale to default or fallback.
      if (!isNumericLocaleAvailable) {
        i18n.numericLocale = isDefaultNumericLocaleAvailable
          ? this.i18n.defaultNumericLocale
          : this.i18n.fallbackNumericLocale;
      } else {
        // Otherwise, only update it to provided or current current locale on change.
        i18n.numericLocale = locale || this.numericLocale;
      }
      this.numericLocale = i18n.numericLocale;
      numeral.locale(i18n.numericLocale);
      // throw error if locale is undefined
      numeral.localeData(i18n.numericLocale);
      // In vue 3 vue-i18n currently is not reactive when changing locales so we need to refresh the components
      // TODO Remove this once AOV3-8139 is done
      if (previousNumericLocale !== this.numericLocale) this.appKey += 1;

      // Inform user if their config locale is being overwritten.
      const defaultNumericLocaleNotSelected = this.numericLocale !== this.i18n.defaultNumericLocale;
      if (isDefaultNumericLocaleAvailable && defaultNumericLocaleNotSelected) {
        console.warn(
          [
            'WARNING: Your current numeric locale settings are overriding your configuration file.',
            'This will change number formatting behaviour versus your configured settings.',
            'If this is expected, disregard.',
          ].join(' ')
        );
        console.warn(`Configured numericLocale is ${this.i18n.defaultNumericLocale}`);
        console.warn(`Current numericLocale is ${this.numericLocale}`);
      }
    },

    setDateLocale({ dateLocale }) {
      const availableDateLocales = this.i18n.availableDateLocales;
      this.availableDateLocales = availableDateLocales;
      const previousDateLocale = this.dateLocale;

      const isDateLocaleAvailable =
        this.dateLocale && includes(availableDateLocales, this.dateLocale);
      const isDefaultDateLocaleAvailable = includes(
        availableDateLocales,
        this.i18n.defaultDateLocale
      );
      // If current current locale is not available, set new current locale to default or fallback.
      if (!isDateLocaleAvailable) {
        i18n.dateLocale = isDefaultDateLocaleAvailable
          ? this.i18n.defaultDateLocale
          : this.i18n.fallbackDateLocale;
      } else {
        // Otherwise, only update it to provided or current current locale on change.
        i18n.dateLocale = dateLocale || this.dateLocale;
      }
      this.dateLocale = i18n.dateLocale;
      // In vue 3 vue-i18n currently is not reactive when changing locales so we need to refresh the components
      // TODO Remove this once AOV3-8139 is done
      if (previousDateLocale !== this.dateLocale) this.appKey += 1;
    },

    refreshUserContext() {
      // Embedded, the shell holds the refresh token and AO can't post to its refresh endpoint
      // cross-origin, so the refresh is brokered through the bridge - ADR-003 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/003-centralize-auth-in-platform-auth-service.md
      const refresh = isEmbedded()
        ? shellBridge()?.refreshJWT()
        : axios.post(STANDALONE_TOKEN_REFRESH_URL);

      if (!refresh) return Promise.reject(new Error('no token refresh available'));

      return Promise.resolve(refresh).then(() => {
        // Marked as already refreshed: a 401 here must not wait on the refresh it belongs to.
        return this.loadUserContext({ _triedRefreshToken: true });
      });
    },

    logout() {
      if (isEmbedded()) throw new Error('the shell owns logout while embedded');

      // There might be multiple login methods configured, so use the one that was used to login
      const currentLoginMethod = this.currentLoginMethod || this.loginMethod;
      const logoutUrl = get(
        authConfig.authenticationPaths,
        `${currentLoginMethod}.logout`,
        '/api/logout'
      );

      return axios.get(logoutUrl).then(res => {
        this.profile = null;
        this.csrfToken = null;

        if (res.data && res.data.redirectUrl) {
          // Some of our auth strategies, like SAML and OpenID, are implemented
          // so the user can close their session within the auth provider, which will then
          // redirect them back to our home page.
          window.location.href = res.data.redirectUrl;
          return Promise.reject();
        }

        return Promise.resolve(
          this.currentLoginMethod === 'ow-auth-okta' ? '/ow-auth/login' : '/login'
        );
      });
    },
    resetState() {
      Object.assign(this, { ...getInitialUserContextState() });
    },
    setAllowTabChange(allowTabChange) {
      this.allowTabChange = allowTabChange;
    },
    // tenant management actions
    setAvailableTenants(availableTenants) {
      this.availableTenants = availableTenants;
    },
    setCurrentTenant(tenantKey) {
      // If no tenantKey provided or tenantKey is not in available list, set to first available
      if (!tenantKey || !this.availableTenants?.some(tenant => tenant.key === tenantKey)) {
        if (this.availableTenants?.length) {
          this.currentTenant = this.availableTenants[0].key;
        }
        return;
      }

      this.currentTenant = tenantKey;
    },
  },
  persist: {
    storage: localStorage,
    key: 'store',
  },
});
